import React, { useRef } from "react";
import { Box, BoxProps } from "@chakra-ui/react";
import { useWebGlShader } from "./useWebGlShader";

// A botanist's hand lens: the image is drawn "cover"-fitted, and a circular lens that follows the
// pointer magnifies it with a little barrel refraction, a glass rim and a soft highlight.
const LENS_FRAGMENT = `
precision mediump float;
uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform sampler2D u_texture;
uniform float u_textureAspect;
varying vec2 v_uv;

vec2 coverUv(vec2 uv) {
  float canvasAspect = u_resolution.x / u_resolution.y;
  vec2 scale = vec2(1.0);
  if (canvasAspect > u_textureAspect) {
    scale.y = u_textureAspect / canvasAspect;
  } else {
    scale.x = canvasAspect / u_textureAspect;
  }
  return (uv - 0.5) * scale + 0.5;
}

void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 mouse = u_mouse;
  // Rest the lens gently near the centre when the pointer is away.
  vec2 restPosition = vec2(0.55, 0.62);
  float away = step(0.999, max(abs(mouse.x - 0.5), abs(mouse.y - 0.5)) * 2.0);
  vec2 center = mix(mouse, restPosition + vec2(sin(u_time * 0.4), cos(u_time * 0.33)) * 0.03, away);

  vec2 d = (uv - center) * vec2(aspect, 1.0);
  float radius = 0.16;
  float dist = length(d);

  vec2 sampleUv = uv;
  float inside = smoothstep(radius, radius - 0.004, dist);
  if (dist < radius) {
    float k = dist / radius;
    float zoom = 1.9 - 0.5 * k * k; // stronger in the middle, easing to the rim
    sampleUv = center + (uv - center) / zoom;
  }
  vec4 color = texture2D(u_texture, coverUv(sampleUv));

  // Rim and highlight.
  float rim = smoothstep(radius - 0.006, radius, dist) * smoothstep(radius + 0.006, radius, dist);
  float highlight = smoothstep(radius, radius - 0.12, length(d - vec2(-0.05, 0.06))) * 0.10;
  vec3 rgb = mix(color.rgb, vec3(0.16, 0.24, 0.18), rim * 0.9) + highlight * inside;
  gl_FragColor = vec4(rgb, color.a);
}`;

export function MagnifierLens({ imageUrl, animate = true, ...props }: BoxProps & { imageUrl: string; animate?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useWebGlShader(canvasRef, { fragmentShader: LENS_FRAGMENT, textureUrl: imageUrl, animate });
  return <Box as="canvas" ref={canvasRef} display="block" w="100%" h="100%" {...props} />;
}
